import type { SessionStats } from '../session/stats';
import { DRILL_TYPE, STORAGE_KEYS, type DrillType } from '../config/constants';

export type StoredSession = {
  id: string;
  startedAt: number;
  durationMs: number;
  userHeightCm: number;
  interrupted: boolean;
  drillType: DrillType;
  stats: SessionStats;
};

type SessionSavedListener = (s: StoredSession) => void;
const savedListeners = new Set<SessionSavedListener>();

type SessionsChangedListener = () => void;
const changedListeners = new Set<SessionsChangedListener>();

/** Subscribe to locally-saved sessions (used by the cloud sync layer to push). */
export function subscribeSessionSaved(fn: SessionSavedListener): () => void {
  savedListeners.add(fn);
  return () => savedListeners.delete(fn);
}

/**
 * Subscribe to any change in the stored set (a new local save OR a remote import).
 * Used by the 2D history view to repaint. Unlike {@link subscribeSessionSaved} this
 * fires for cloud-imported sessions too (which save with `notify=false`).
 */
export function subscribeSessionsChanged(fn: SessionsChangedListener): () => void {
  changedListeners.add(fn);
  return () => changedListeners.delete(fn);
}

export function saveSession(s: StoredSession, notify = true): void {
  try {
    localStorage.setItem(STORAGE_KEYS.sessionPrefix + s.id, JSON.stringify(s));
    const idx = loadIndex();
    if (!idx.includes(s.id)) {
      idx.push(s.id);
      localStorage.setItem(STORAGE_KEYS.sessionIndex, JSON.stringify(idx));
    }
  } catch (err) {
    console.warn('saveSession failed', err);
  }
  if (notify) for (const fn of savedListeners) fn(s);
  for (const fn of changedListeners) fn();
}

/**
 * Shallow shape check for an untrusted (remote) session doc. Validates the
 * top-level fields that storage and sorting rely on; `stats` is only required
 * to be an object — its per-drill counters are not deep-validated.
 */
export function isValidStoredSession(s: unknown): s is StoredSession {
  if (!s || typeof s !== 'object') return false;
  const o = s as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    o.id.length > 0 &&
    Number.isFinite(o.startedAt) &&
    Number.isFinite(o.durationMs) &&
    Number.isFinite(o.userHeightCm) &&
    typeof o.interrupted === 'boolean' &&
    Object.values(DRILL_TYPE).includes(o.drillType as DrillType) &&
    typeof o.stats === 'object' &&
    o.stats !== null
  );
}

/** Merge remote sessions into local storage (union by id). Does not re-notify. */
export function importSessions(list: unknown[]): void {
  for (const s of list) {
    if (!isValidStoredSession(s)) {
      console.warn('importSessions: skipping malformed remote session', s);
      continue;
    }
    if (!loadSession(s.id)) saveSession(s, false);
  }
}

export function loadSession(id: string): StoredSession | null {
  const raw = localStorage.getItem(STORAGE_KEYS.sessionPrefix + id);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
}

export function listSessions(): StoredSession[] {
  const idx = loadIndex();
  const out: StoredSession[] = [];
  for (const id of idx) {
    const s = loadSession(id);
    if (s) out.push(s);
  }
  out.sort((a, b) => b.startedAt - a.startedAt);
  return out;
}

/** Remove one locally-stored session (and its index entry), then notify listeners. */
export function deleteSession(id: string): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.sessionPrefix + id);
    const idx = loadIndex().filter((x) => x !== id);
    localStorage.setItem(STORAGE_KEYS.sessionIndex, JSON.stringify(idx));
  } catch (err) {
    console.warn('deleteSession failed', err);
  }
  for (const fn of changedListeners) fn();
}

/** Remove every locally-stored session (and the index), then notify listeners. */
export function clearAllSessions(): void {
  try {
    for (const id of loadIndex()) localStorage.removeItem(STORAGE_KEYS.sessionPrefix + id);
    localStorage.removeItem(STORAGE_KEYS.sessionIndex);
  } catch (err) {
    console.warn('clearAllSessions failed', err);
  }
  for (const fn of changedListeners) fn();
}

export function exportSessionJSON(s: StoredSession): string {
  return JSON.stringify(s, null, 2);
}

/** Serialize every stored session (newest first) for a full backup/export. */
export function exportAllSessionsJSON(): string {
  return JSON.stringify(listSessions(), null, 2);
}

function loadIndex(): string[] {
  const raw = localStorage.getItem(STORAGE_KEYS.sessionIndex);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}
